/**
 * VaultStack Onboarding Flow
 * Guides users through signup → business setup
 * 
 * Simple, clear, step-by-step guidance
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, ArrowRight, User, Building2, ClipboardList } from 'lucide-react';

export function OnboardingFlow() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'auth' | 'business' | 'industry' | 'complete'>('auth');
  const [loading, setLoading] = useState(false);
  
  // Auth state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  
  // Business state
  const [businessName, setBusinessName] = useState('');
  const [serviceArea, setServiceArea] = useState('');
  const [isAustralian, setIsAustralian] = useState(true);
  
  // Industry state
  const [industry, setIndustry] = useState('mowing');

  // Check if user is already logged in
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setStep('business');
      }
    };
    checkSession();
  }, []);

  const handleSignup = async () => {
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName || 'VaultStack User',
            role: 'admin'
          }
        }
      });

      if (error) throw error;

      toast.success('Account created! Let\'s set up your business.');
      setStep('business');
    } catch (error) {
      console.error('Signup error:', error);
      toast.error(error instanceof Error ? error.message : 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin
        }
      });

      if (error) throw error;
    } catch (error) {
      console.error('Google signup error:', error);
      toast.error('Google sign-in failed');
      setLoading(false);
    }
  };

  const handleBusinessSetup = async () => {
    if (!businessName || !serviceArea) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('You must be logged in');
        setLoading(false);
        return;
      }

      const { error } = await supabase
        .from('business_config')
        .insert([{
          user_id: user.id,
          biz_name: businessName,
          biz_type: industry,
          service_area: serviceArea,
          is_australian_business: isAustralian,
          onboarding_completed: true,
        }]);

      if (error) throw error;

      toast.success('Business setup complete! 🎉');
      setStep('complete');
    } catch (error) {
      console.error('Business setup error:', error);
      toast.error('Setup failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('onboardingSkipped', 'true');
    toast.success('Setup skipped - You can complete this later in Settings');
    navigate('/admin');
  };

  // Step 1: Authentication
  if (step === 'auth') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-slate-100 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-900 rounded-2xl mb-4 mx-auto">
              <User className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Welcome to VaultStack</CardTitle>
            <CardDescription>Create your account to get started</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-bold mb-1">Full Name (Optional)</label>
              <Input
                placeholder="John Smith"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="h-12"
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-1">Email</label>
              <Input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12"
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-1">Password</label>
              <Input
                type="password"
                placeholder="Min 6 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12"
                minLength={6}
              />
            </div>

            <Button
              onClick={handleSignup}
              disabled={loading}
              className="w-full h-14 bg-slate-900 hover:bg-slate-800 text-white font-black uppercase tracking-widest"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating Account...
                </>
              ) : (
                <>
                  Create Account
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-500">Or continue with</span>
              </div>
            </div>

            <Button
              onClick={handleGoogleSignup}
              disabled={loading}
              variant="outline"
              className="w-full h-14"
            >
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5 mr-2" />
              Continue with Google
            </Button>

            <div className="text-center">
              <button
                onClick={handleSkip}
                className="text-xs text-slate-400 hover:text-purple-600 underline underline-offset-4 font-bold"
              >
                Skip for now
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 2: Business Details
  if (step === 'business') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-slate-100 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-900 rounded-2xl mb-4 mx-auto">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Tell Us About Your Business</CardTitle>
            <CardDescription>This will be used for your customer-facing pages</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-bold mb-1">Business Name</label>
              <Input
                placeholder="e.g. Joe's Mowing"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                className="h-12"
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-1">Service Area</label>
              <Input
                placeholder="e.g. Brisbane Northside"
                value={serviceArea}
                onChange={(e) => setServiceArea(e.target.value)}
                className="h-12"
              />
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isAustralian}
                onChange={(e) => setIsAustralian(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm font-bold">Australian Business</span>
            </label>

            <Button
              onClick={handleBusinessSetup}
              disabled={loading || !businessName || !serviceArea}
              className="w-full h-14 bg-purple-600 hover:bg-purple-700 text-white font-black uppercase tracking-widest"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Setting Up...
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>

            <div className="text-center">
              <button
                onClick={handleSkip}
                className="text-xs text-slate-400 hover:text-purple-600 underline underline-offset-4 font-bold"
              >
                Skip for now
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 3: Industry Selection
  if (step === 'industry') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-slate-100 p-4">
        <Card className="w-full max-w-2xl">
          <CardHeader className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-900 rounded-2xl mb-4 mx-auto">
              <ClipboardList className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Select Your Industry</CardTitle>
            <CardDescription>We'll preload templates specific to your trade</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { value: 'mowing', label: 'Lawn Mowing', icon: '🌱' },
                { value: 'detailing', label: 'Car Detailing', icon: '🚗' },
                { value: 'handyman', label: 'Handyman', icon: '🔧' },
                { value: 'painting', label: 'Painting', icon: '🎨' },
                { value: 'cleaning', label: 'Cleaning Services', icon: '🧹' },
                { value: 'support_worker', label: 'Support Worker', icon: '🏥' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setIndustry(option.value)}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    industry === option.value
                      ? 'border-purple-600 bg-purple-50'
                      : 'border-slate-200 hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{option.icon}</span>
                    <span className="font-bold">{option.label}</span>
                    {industry === option.value && (
                      <CheckCircle2 className="w-5 h-5 text-purple-600 ml-auto" />
                    )}
                  </div>
                </button>
              ))}
            </div>

            <Button
              onClick={handleBusinessSetup}
              disabled={loading}
              className="w-full h-14 bg-teal-500 hover:bg-teal-600 text-white font-black uppercase tracking-widest"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Completing Setup...
                </>
              ) : (
                <>
                  Complete Setup
                  <CheckCircle2 className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>

            <div className="text-center">
              <button
                onClick={handleSkip}
                className="text-xs text-slate-400 hover:text-purple-600 underline underline-offset-4 font-bold"
              >
                Skip for now
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 4: Complete
  if (step === 'complete') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-slate-100 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500 rounded-full mb-4 mx-auto">
              <CheckCircle2 className="w-10 h-10 text-white" />
            </div>
            <CardTitle className="text-2xl">You're All Set! 🎉</CardTitle>
            <CardDescription>Your VaultStack account is ready</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-bold text-green-900 mb-2">What's Next?</h4>
              <ul className="text-sm text-green-800 space-y-1 list-disc list-inside">
                <li>Access your admin dashboard</li>
                <li>Create custom templates</li>
                <li>Manage inventory & bookings</li>
                <li>Generate quotes & invoices</li>
              </ul>
            </div>

            <Button
              onClick={() => navigate('/admin')}
              className="w-full h-14 bg-slate-900 hover:bg-slate-800 text-white font-black uppercase tracking-widest"
            >
              Go to Dashboard
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}